import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { MessageSquare, Loader2, Check, RotateCcw, Languages, GripVertical, Edit2, Trash2, Plus, Copy, ClipboardPaste } from 'lucide-react'
import { Reorder, useDragControls } from 'framer-motion'
import { useBot } from '@/shared/hooks/useBots'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useBackButton } from '@/shared/hooks/useBackButton'
import { botsApi } from '@/shared/api/bots'
import type { CustomBlockButton } from '@/shared/types'

const SUPPORTED_LANGUAGES = [
  { code: 'ru', name: 'Русский' },
  { code: 'en', name: 'English' },
  { code: 'uk', name: 'Українська' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
  { code: 'it', name: 'Italiano' },
  { code: 'pt', name: 'Português' },
  { code: 'ar', name: 'العربية' },
  { code: 'zh', name: '中文' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'hi', name: 'हिन्दी' },
]

const DEFAULT_TEMPLATE = {
  ru: {
    description_text: 'Привет, ;fullname;! Выполните эти задания:',
    button_text_channel: 'Подписаться на ;name;',
    button_text_bot: 'Запустить ;name;',
    button_text_boost: 'Бустить ;name;',
    completion_message_text: 'Спасибо!\n<b>Доступ к функциям бота открыт</b>',
  },
  en: {
    description_text: 'Hello, ;fullname;! Complete these tasks:',
    button_text_channel: 'Join ;name;',
    button_text_bot: 'Start ;name;',
    button_text_boost: 'Boost ;name;',
    completion_message_text: 'Thank you!\n<b>Bot access granted</b>',
  },
}

const DEFAULT_BUTTON: CustomBlockButton = {
  order: 0,
  text: {
    ru: '✅ Я выполнил все задания',
    en: '✅ I completed all tasks'
  },
  callback_data: 'task_completed',
  row_index: 0
}

export default function BotCustomBlock() {
  const { botId } = useParams<{ botId: string }>()
  const navigate = useNavigate()
  const { isReady, hapticFeedback, hapticNotification, showMainButton, hideMainButton, setMainButtonLoading } = useTelegram()
  const { data: bot } = useBot(botId)

  // Multi-language state
  const [selectedLanguage, setSelectedLanguage] = useState<string>('ru')
  const [fallbackLanguageCode, setFallbackLanguageCode] = useState<string>('ru')
  const [descriptionText, setDescriptionText] = useState<Record<string, string>>({})
  const [buttonTextChannel, setButtonTextChannel] = useState<Record<string, string>>({})
  const [buttonTextBot, setButtonTextBot] = useState<Record<string, string>>({})
  const [buttonTextBoost, setButtonTextBoost] = useState<Record<string, string>>({})
  const [completionMessageText, setCompletionMessageText] = useState<Record<string, string>>({})

  // Buttons array (replaces single completion button)
  const [buttons, setButtons] = useState<CustomBlockButton[]>([])
  const [editingButtonId, setEditingButtonId] = useState<number | null>(null)

  const [channelsPerRow, setChannelsPerRow] = useState<number>(2)
  const [showSuccess, setShowSuccess] = useState(false)
  const [error, setError] = useState<string>('')
  const [isFetching, setIsFetching] = useState(true)
  const [formatMode, setFormatMode] = useState<'html' | 'markdown'>('html')
  const [hasClipboard, setHasClipboard] = useState(false)
  const [clipboardMessage, setClipboardMessage] = useState<string>('')

  const handleSaveRef = useRef<() => Promise<void>>()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const hasFetchedRef = useRef(false)
  const pendingReorderRef = useRef<CustomBlockButton[] | null>(null)
  const newlyAddedButtonIndexRef = useRef<number | null>(null) // Track newly added button that hasn't been saved yet

  // Fetch existing template
  useEffect(() => {
    const fetchTemplate = async () => {
      if (!botId || hasFetchedRef.current) return

      hasFetchedRef.current = true
      setIsFetching(true)
      console.log('[CustomBlock] Starting fetch for botId:', botId)

      try {
        const template = await botsApi.getCustomBlockTemplate(botId)
        console.log('[CustomBlock] Fetched template:', template)

        setDescriptionText(template.description_text || {})
        setButtonTextChannel(template.button_text_channel || {})
        setButtonTextBot(template.button_text_bot || {})
        setButtonTextBoost(template.button_text_boost || {})
        setCompletionMessageText(template.completion_message_text || {})
        setChannelsPerRow(template.channels_per_row || 2)
        setFallbackLanguageCode(template.fallback_language_code || 'ru')
        setFormatMode(template.mode || 'html')

        // Handle both new buttons array format and old single button format
        if (template.buttons && Array.isArray(template.buttons) && template.buttons.length > 0) {
          // New format: buttons array exists - normalize row_index to match order
          const normalizedButtons = template.buttons.map((btn, i) => ({
            ...btn,
            order: i,
            row_index: i
          }))
          setButtons(normalizedButtons)
        } else if ((template as any).completion_button_text && Object.keys((template as any).completion_button_text).length > 0) {
          // Old format: migrate single button to array
          setButtons([{
            order: 0,
            text: (template as any).completion_button_text,
            callback_data: (template as any).completion_button_callback_data || 'task_completed',
            row_index: 0
          }])
        } else {
          // No buttons - use default
          setButtons([{ ...DEFAULT_BUTTON }])
        }
      } catch (error) {
        console.log('[CustomBlock] No template found, using defaults:', error)
        // If no template exists, use default values
        setDescriptionText({
          ru: DEFAULT_TEMPLATE.ru.description_text,
          en: DEFAULT_TEMPLATE.en.description_text,
        })
        setButtonTextChannel({
          ru: DEFAULT_TEMPLATE.ru.button_text_channel,
          en: DEFAULT_TEMPLATE.en.button_text_channel,
        })
        setButtonTextBot({
          ru: DEFAULT_TEMPLATE.ru.button_text_bot,
          en: DEFAULT_TEMPLATE.en.button_text_bot,
        })
        setButtonTextBoost({
          ru: DEFAULT_TEMPLATE.ru.button_text_boost,
          en: DEFAULT_TEMPLATE.en.button_text_boost,
        })
        setCompletionMessageText({
          ru: DEFAULT_TEMPLATE.ru.completion_message_text,
          en: DEFAULT_TEMPLATE.en.completion_message_text,
        })
        setButtons([{ ...DEFAULT_BUTTON }])
        setChannelsPerRow(2)
        setFormatMode('html')
        setFallbackLanguageCode('ru')
      } finally {
        setIsFetching(false)
      }
    }

    fetchTemplate()
  }, [botId])

  // Reset success state after 2 seconds
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [showSuccess])

  // Check localStorage for clipboard data on mount
  useEffect(() => {
    setHasClipboard(!!localStorage.getItem('customBlockClipboard'))
  }, [])

  // Reset clipboard message after 2 seconds
  useEffect(() => {
    if (clipboardMessage) {
      const timer = setTimeout(() => {
        setClipboardMessage('')
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [clipboardMessage])

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
    }
  }, [descriptionText, selectedLanguage])

  // Button management functions
  const handleAddButton = () => {
    if (buttons.length >= 10) return

    hapticFeedback('medium')

    const newButton: CustomBlockButton = {
      order: buttons.length,
      text: {
        [selectedLanguage]: 'Новая кнопка'
      },
      callback_data: `button_${buttons.length}`,
      row_index: buttons.length
    }

    const newIndex = buttons.length
    setButtons([...buttons, newButton])
    setEditingButtonId(newIndex)
    newlyAddedButtonIndexRef.current = newIndex // Track that this is a newly added button
  }

  const handleEditButton = (index: number) => {
    hapticFeedback('light')
    setEditingButtonId(index)
  }

  const handleSaveButton = (index: number, updatedButton: CustomBlockButton) => {
    hapticFeedback('medium')

    const newButtons = [...buttons]
    // Ensure row_index matches order
    newButtons[index] = {
      ...updatedButton,
      order: index,
      row_index: index
    }

    setButtons(newButtons)
    setEditingButtonId(null)
    newlyAddedButtonIndexRef.current = null // Clear the newly added button tracking
  }

  const handleCancelEdit = (index: number) => {
    hapticFeedback('light')

    // If this is a newly added button that hasn't been saved, remove it
    if (newlyAddedButtonIndexRef.current === index) {
      const newButtons = buttons.filter((_, i) => i !== index)
      // Re-normalize order and row_index values
      newButtons.forEach((btn, i) => {
        btn.order = i
        btn.row_index = i
      })
      setButtons(newButtons)
      newlyAddedButtonIndexRef.current = null
    }

    setEditingButtonId(null)
  }

  const handleDeleteButton = (index: number) => {
    hapticFeedback('medium')

    const newButtons = buttons.filter((_, i) => i !== index)

    // Re-normalize order and row_index values (each button on separate row)
    newButtons.forEach((btn, i) => {
      btn.order = i
      btn.row_index = i
    })

    setButtons(newButtons)
    if (editingButtonId === index) {
      setEditingButtonId(null)
    }
  }

  const handleReorder = (newOrder: CustomBlockButton[]) => {
    // Store pending reorder but don't apply yet
    pendingReorderRef.current = newOrder
  }

  const handleDragEnd = () => {
    // Apply the reorder when drag ends
    if (pendingReorderRef.current) {
      const reorderedButtons = pendingReorderRef.current.map((btn, i) => ({
        ...btn,
        order: i,
        row_index: i
      }))
      setButtons(reorderedButtons)
      pendingReorderRef.current = null
      hapticFeedback('medium')
    }
  }

  const handleResetToDefault = () => {
    hapticFeedback('medium')
    setDescriptionText({
      ru: DEFAULT_TEMPLATE.ru.description_text,
      en: DEFAULT_TEMPLATE.en.description_text,
    })
    setButtonTextChannel({
      ru: DEFAULT_TEMPLATE.ru.button_text_channel,
      en: DEFAULT_TEMPLATE.en.button_text_channel,
    })
    setButtonTextBot({
      ru: DEFAULT_TEMPLATE.ru.button_text_bot,
      en: DEFAULT_TEMPLATE.en.button_text_bot,
    })
    setButtonTextBoost({
      ru: DEFAULT_TEMPLATE.ru.button_text_boost,
      en: DEFAULT_TEMPLATE.en.button_text_boost,
    })
    setCompletionMessageText({
      ru: DEFAULT_TEMPLATE.ru.completion_message_text,
      en: DEFAULT_TEMPLATE.en.completion_message_text,
    })
    setButtons([{ ...DEFAULT_BUTTON }])
    setChannelsPerRow(2)
    setFallbackLanguageCode('ru')
  }

  const handleCopyConfig = () => {
    hapticFeedback('medium')
    const configData = {
      description_text: descriptionText,
      button_text_channel: buttonTextChannel,
      button_text_bot: buttonTextBot,
      button_text_boost: buttonTextBoost,
      completion_message_text: completionMessageText,
      buttons: buttons,
      fallback_language_code: fallbackLanguageCode,
      channels_per_row: channelsPerRow,
      mode: formatMode,
    }
    localStorage.setItem('customBlockClipboard', JSON.stringify(configData))
    setHasClipboard(true)
    setClipboardMessage('Настройки скопированы')
    hapticNotification('success')
  }

  const handlePasteConfig = () => {
    const saved = localStorage.getItem('customBlockClipboard')
    if (!saved) return

    hapticFeedback('medium')
    try {
      const data = JSON.parse(saved)
      setDescriptionText(data.description_text || {})
      setButtonTextChannel(data.button_text_channel || {})
      setButtonTextBot(data.button_text_bot || {})
      setButtonTextBoost(data.button_text_boost || {})
      setCompletionMessageText(data.completion_message_text || {})
      setButtons(data.buttons || [{ ...DEFAULT_BUTTON }])
      setFallbackLanguageCode(data.fallback_language_code || 'ru')
      setChannelsPerRow(data.channels_per_row || 2)
      setFormatMode(data.mode || 'html')
      setClipboardMessage('Настройки вставлены')
      hapticNotification('success')
    } catch (e) {
      console.error('Failed to parse clipboard data:', e)
      setError('Ошибка при вставке настроек')
      hapticNotification('error')
    }
  }

  const handleSave = async () => {
    if (!bot || !botId) return

    setError('')
    hapticFeedback('medium')
    setMainButtonLoading(true)

    // Validation
    const hasDescriptionText = Object.values(descriptionText).some(text => text.trim())
    const hasButtonTextChannel = Object.values(buttonTextChannel).some(text => text.trim())
    const hasButtonTextBot = Object.values(buttonTextBot).some(text => text.trim())
    const hasButtonTextBoost = Object.values(buttonTextBoost).some(text => text.trim())
    const hasCompletionMessageText = Object.values(completionMessageText).some(text => text.trim())

    if (!hasDescriptionText || !hasButtonTextChannel || !hasButtonTextBot || !hasButtonTextBoost || !hasCompletionMessageText) {
      setError('Основные поля обязательны для заполнения хотя бы на одном языке')
      hapticNotification('error')
      setMainButtonLoading(false)
      return
    }

    // Validate all buttons
    for (let i = 0; i < buttons.length; i++) {
      const button = buttons[i]

      // Check if at least one language has text
      const hasText = Object.values(button.text).some(text => text.trim())
      if (!hasText) {
        setError(`Кнопка ${i + 1}: добавьте текст хотя бы на одном языке`)
        hapticNotification('error')
        setMainButtonLoading(false)
        return
      }

      // Check callback_data
      if (!button.callback_data.trim()) {
        setError(`Кнопка ${i + 1}: укажите callback_data`)
        hapticNotification('error')
        setMainButtonLoading(false)
        return
      }
    }

    try {
      // Normalize buttons to ensure all required fields are properly set
      const normalizedButtons = buttons.map((btn, index) => ({
        order: Number(index), // Ensure order is a number
        text: btn.text || {},
        callback_data: btn.callback_data || '',
        row_index: Number(index) // Ensure row_index is a number
      }))

      const data = {
        bot_id: botId,
        description_text: descriptionText,
        button_text_channel: buttonTextChannel,
        button_text_bot: buttonTextBot,
        button_text_boost: buttonTextBoost,
        completion_message_text: completionMessageText,
        buttons: normalizedButtons,
        fallback_language_code: fallbackLanguageCode,
        channels_per_row: channelsPerRow,
        mode: formatMode,
      }

      console.log('[CustomBlock] Saving data:', data)

      // Try to update first, if fails, create new
      try {
        const result = await botsApi.updateCustomBlockTemplate(botId, data)
        console.log('[CustomBlock] Update result:', result)
      } catch (updateError) {
        console.log('[CustomBlock] Update failed, trying create:', updateError)
        const result = await botsApi.createCustomBlockTemplate(botId, data)
        console.log('[CustomBlock] Create result:', result)
      }

      hapticNotification('success')
      setShowSuccess(true)
      setMainButtonLoading(false)

      hideMainButton()
      document.body.style.backgroundColor = ''
      window.scrollTo(0, 0)

      navigate(`/bots/${botId}`, {
        replace: true,
        state: { successMessage: 'Бот успешно обновлен' }
      })
    } catch (error) {
      console.error('Failed to save custom block:', error)
      setError('Ошибка при сохранении настроек блока')
      hapticNotification('error')
      setMainButtonLoading(false)
    }
  }

  // Update handleSaveRef
  handleSaveRef.current = handleSave

  // Back button
  useBackButton({
    enabled: true,
    onBack: () => {
      hideMainButton()
      document.body.style.backgroundColor = ''
      navigate(`/bots/${botId}`)
    }
  })

  // Main button
  useEffect(() => {
    if (!isReady) return

    showMainButton('Сохранить', () => {
      handleSaveRef.current?.()
    }, '#5288c1', '#ffffff')

    return () => {
      hideMainButton()
    }
  }, [isReady, showMainButton, hideMainButton])

  if (isFetching) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-tg-bg">
        <Loader2 className="w-8 h-8 animate-spin text-[#5288c1]" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Блок заданий</h1>
              <p className="text-sm text-tg-hint mt-0.5">@{bot?.username}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">

        {/* Language Selector */}
        <div className="bg-tg-secondary-bg rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Languages className="w-5 h-5 text-purple-400" />
            <h2 className="font-semibold text-tg-text">Язык интерфейса</h2>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {SUPPORTED_LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => {
                  setSelectedLanguage(lang.code)
                  hapticFeedback('light')
                }}
                className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                  selectedLanguage === lang.code
                    ? 'bg-[#5288c1] text-white'
                    : 'bg-tg-bg text-tg-hint hover:bg-tg-hint/10'
                }`}
              >
                {lang.name}
              </button>
            ))}
          </div>
          <div className="mt-3">
            <label className="block text-xs text-tg-hint mb-2">
              Резервный язык (если текст не найден на языке пользователя)
            </label>
            <select
              value={fallbackLanguageCode}
              onChange={(e) => {
                setFallbackLanguageCode(e.target.value)
                hapticFeedback('light')
              }}
              className="w-full px-3 py-2 bg-tg-bg text-tg-text rounded-lg border-2 border-transparent focus:border-purple-400 focus:outline-none"
            >
              {SUPPORTED_LANGUAGES.map(lang => (
                <option key={lang.code} value={lang.code}>{lang.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Description Section Header */}
        <div className="pt-2">
          <h3 className="text-sm font-semibold text-tg-hint uppercase tracking-wide px-1">
            Текст описания ({selectedLanguage.toUpperCase()})
          </h3>
        </div>

        {/* Description Text Block */}
        <div className="bg-tg-secondary-bg rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 pt-4 pb-3 border-b border-tg-secondary-bg">
            <div className="flex items-center justify-start">
              <div className="flex bg-tg-bg rounded-lg p-0.5">
                <button
                  onClick={() => {
                    setFormatMode('html')
                    hapticFeedback('light')
                  }}
                  className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${
                    formatMode === 'html'
                      ? 'bg-[#5288c1] text-white'
                      : 'text-tg-hint'
                  }`}
                >
                  HTML
                </button>
                <button
                  onClick={() => {
                    setFormatMode('markdown')
                    hapticFeedback('light')
                  }}
                  className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${
                    formatMode === 'markdown'
                      ? 'bg-[#5288c1] text-white'
                      : 'text-tg-hint'
                  }`}
                >
                  Markdown
                </button>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="text-xs text-tg-hint mb-3 space-y-1">
              <p>Доступные переменные: ;fullname; ;username;</p>
              {formatMode === 'html' ? (
                <p>Форматирование: <code className="bg-tg-bg px-1 rounded">&lt;b&gt;жирный&lt;/b&gt;</code> <code className="bg-tg-bg px-1 rounded">&lt;i&gt;курсив&lt;/i&gt;</code></p>
              ) : (
                <p>Форматирование: <code className="bg-tg-bg px-1 rounded">**жирный**</code> <code className="bg-tg-bg px-1 rounded">*курсив*</code></p>
              )}
            </div>
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={descriptionText[selectedLanguage] || ''}
                onChange={(e) => {
                  setDescriptionText({ ...descriptionText, [selectedLanguage]: e.target.value })
                  setError('')
                }}
                onFocus={() => hapticFeedback('light')}
                spellCheck={false}
                className="w-full px-4 py-3 text-base border-2 border-transparent rounded-lg focus:border-[#5288c1] focus:outline-none transition-colors bg-tg-bg placeholder:text-tg-hint resize-none overflow-hidden"
                style={{
                  color: 'transparent',
                  caretColor: 'var(--tg-theme-text-color)',
                  WebkitTextFillColor: 'transparent'
                }}
                placeholder={`Текст описания на ${selectedLanguage === 'ru' ? 'русском' : 'английском'} языке`}
                rows={3}
              />
              <div
                className="absolute inset-0 px-4 py-3 text-base pointer-events-none whitespace-pre-wrap break-words overflow-hidden z-10"
                style={{
                  font: 'inherit',
                  lineHeight: 'inherit',
                  color: 'var(--tg-theme-text-color)'
                }}
                dangerouslySetInnerHTML={{
                  __html: (descriptionText[selectedLanguage] || '')
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/;(fullname|username);/gi, (match) => {
                      return `<span style="background: linear-gradient(90deg, #ff0080, #ff8c00, #40e0d0, #4169e1, #9370db); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; text-decoration: none;">${match}</span>`
                    })
                }}
              />
            </div>
          </div>
        </div>

        {/* Button Texts Section Header */}
        <div className="pt-2">
          <h3 className="text-sm font-semibold text-tg-hint uppercase tracking-wide px-1">
            Тексты кнопок ({selectedLanguage.toUpperCase()})
          </h3>
        </div>

        {/* Button Texts Block */}
        <div className="bg-tg-secondary-bg rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 space-y-3">
            <p className="text-xs text-tg-hint mb-3">
              Доступные переменные: ;name; (название канала/бота), ;num; (номер задания в блоке)
            </p>

            <div>
              <label className="block text-xs text-tg-hint mb-1.5">
                Кнопка подписки на канал
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={buttonTextChannel[selectedLanguage] || ''}
                  onChange={(e) => {
                    setButtonTextChannel({ ...buttonTextChannel, [selectedLanguage]: e.target.value })
                    setError('')
                  }}
                  onFocus={() => hapticFeedback('light')}
                  spellCheck={false}
                  className="w-full px-4 py-2.5 text-base border-2 border-transparent rounded-lg focus:border-[#5288c1] focus:outline-none transition-colors bg-tg-bg placeholder:text-tg-hint"
                  style={{
                    color: 'transparent',
                    caretColor: 'var(--tg-theme-text-color)',
                    WebkitTextFillColor: 'transparent'
                  }}
                  placeholder={selectedLanguage === 'ru' ? 'Подписаться на ;name;' : 'Join ;name;'}
                />
                <div
                  className="absolute inset-0 px-4 py-2.5 text-base pointer-events-none z-10 flex items-center"
                  style={{ color: 'var(--tg-theme-text-color)' }}
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: (buttonTextChannel[selectedLanguage] || '')
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')
                        .replace(/;(name|num);/gi, (match) => {
                          return `<span style="background: linear-gradient(90deg, #ff0080, #ff8c00, #40e0d0, #4169e1, #9370db); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; text-decoration: none;">${match}</span>`
                        })
                    }}
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs text-tg-hint mb-1.5">
                Кнопка запуска бота
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={buttonTextBot[selectedLanguage] || ''}
                  onChange={(e) => {
                    setButtonTextBot({ ...buttonTextBot, [selectedLanguage]: e.target.value })
                    setError('')
                  }}
                  onFocus={() => hapticFeedback('light')}
                  spellCheck={false}
                  className="w-full px-4 py-2.5 text-base border-2 border-transparent rounded-lg focus:border-[#5288c1] focus:outline-none transition-colors bg-tg-bg placeholder:text-tg-hint"
                  style={{
                    color: 'transparent',
                    caretColor: 'var(--tg-theme-text-color)',
                    WebkitTextFillColor: 'transparent'
                  }}
                  placeholder={selectedLanguage === 'ru' ? 'Запустить ;name;' : 'Start ;name;'}
                />
                <div
                  className="absolute inset-0 px-4 py-2.5 text-base pointer-events-none z-10 flex items-center"
                  style={{ color: 'var(--tg-theme-text-color)' }}
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: (buttonTextBot[selectedLanguage] || '')
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')
                        .replace(/;(name|num);/gi, (match) => {
                          return `<span style="background: linear-gradient(90deg, #ff0080, #ff8c00, #40e0d0, #4169e1, #9370db); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; text-decoration: none;">${match}</span>`
                        })
                    }}
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs text-tg-hint mb-1.5">
                Кнопка бустинга канала
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={buttonTextBoost[selectedLanguage] || ''}
                  onChange={(e) => {
                    setButtonTextBoost({ ...buttonTextBoost, [selectedLanguage]: e.target.value })
                    setError('')
                  }}
                  onFocus={() => hapticFeedback('light')}
                  spellCheck={false}
                  className="w-full px-4 py-2.5 text-base border-2 border-transparent rounded-lg focus:border-[#5288c1] focus:outline-none transition-colors bg-tg-bg placeholder:text-tg-hint"
                  style={{
                    color: 'transparent',
                    caretColor: 'var(--tg-theme-text-color)',
                    WebkitTextFillColor: 'transparent'
                  }}
                  placeholder={selectedLanguage === 'ru' ? 'Бустить ;name;' : 'Boost ;name;'}
                />
                <div
                  className="absolute inset-0 px-4 py-2.5 text-base pointer-events-none z-10 flex items-center"
                  style={{ color: 'var(--tg-theme-text-color)' }}
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: (buttonTextBoost[selectedLanguage] || '')
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')
                        .replace(/;(name|num);/gi, (match) => {
                          return `<span style="background: linear-gradient(90deg, #ff0080, #ff8c00, #40e0d0, #4169e1, #9370db); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; text-decoration: none;">${match}</span>`
                        })
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Channels Per Row Setting */}
            <div className="pt-3 mt-3 border-t border-tg-secondary-bg">
              <label className="block text-xs text-tg-hint mb-2">
                Заданий в строке
              </label>
              <div className="flex gap-2">
                {[1, 2, 3].map(num => (
                  <button
                    key={num}
                    onClick={() => {
                      setChannelsPerRow(num)
                      hapticFeedback('light')
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      channelsPerRow === num
                        ? 'bg-[#5288c1] text-white'
                        : 'bg-tg-bg text-tg-hint hover:bg-tg-hint/10'
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Buttons Section Header */}
        <div className="pt-2">
          <h3 className="text-sm font-semibold text-tg-hint uppercase tracking-wide px-1">
            Кнопки под заданиями ({selectedLanguage.toUpperCase()})
          </h3>
        </div>

        {/* Buttons Array Manager - SEPARATE BLOCK */}
        <div className="bg-tg-secondary-bg rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 space-y-4">
            {/* Add Button */}
            <div className="flex items-center justify-start">
              <button
                onClick={handleAddButton}
                disabled={buttons.length >= 10}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  buttons.length >= 10
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[#5288c1] text-white hover:bg-[#4a7aa8]'
                }`}
              >
                <Plus className="w-4 h-4" />
                Добавить
              </button>
            </div>

            {/* Buttons List with Drag & Drop */}
            {buttons.length > 0 ? (
              <Reorder.Group
                as="div"
                axis="y"
                values={buttons}
                onReorder={handleReorder}
                className="space-y-2"
                layoutScroll={false}
              >
                {buttons.map((button, index) => {
                  const isEditing = editingButtonId === index
                  const stableKey = `btn-${button.order}-${button.callback_data.substring(0, 10)}`

                  return (
                    <ButtonItem
                      key={stableKey}
                      button={button}
                      index={index}
                      isEditing={isEditing}
                      selectedLanguage={selectedLanguage}
                      onEdit={handleEditButton}
                      onDelete={handleDeleteButton}
                      onSave={handleSaveButton}
                      onCancelEdit={() => handleCancelEdit(index)}
                      onDragEnd={handleDragEnd}
                      hapticFeedback={hapticFeedback}
                    />
                  )
                })}
              </Reorder.Group>
            ) : (
              <div className="text-center py-8 text-tg-hint">
                <p className="text-sm">Нет кнопок</p>
                <p className="text-xs mt-1">Добавьте кнопки для завершения заданий</p>
              </div>
            )}
          </div>
        </div>

        {/* Completion Message Section Header */}
        <div className="pt-2">
          <h3 className="text-sm font-semibold text-tg-hint uppercase tracking-wide px-1">
            Сообщение о завершении ({selectedLanguage.toUpperCase()})
          </h3>
        </div>

        {/* Completion Message Block */}
        <div className="bg-tg-secondary-bg rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 pt-4 pb-3 border-b border-tg-secondary-bg">
            <div className="flex items-center justify-start">
              <div className="flex bg-tg-bg rounded-lg p-0.5">
                <button
                  onClick={() => {
                    setFormatMode('html')
                    hapticFeedback('light')
                  }}
                  className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${
                    formatMode === 'html'
                      ? 'bg-[#5288c1] text-white'
                      : 'text-tg-hint'
                  }`}
                >
                  HTML
                </button>
                <button
                  onClick={() => {
                    setFormatMode('markdown')
                    hapticFeedback('light')
                  }}
                  className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${
                    formatMode === 'markdown'
                      ? 'bg-[#5288c1] text-white'
                      : 'text-tg-hint'
                  }`}
                >
                  Markdown
                </button>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="text-xs text-tg-hint mb-3 space-y-1">
              <p>Доступные переменные: ;fullname; ;username;</p>
              <p className="text-tg-hint/70">Отображается когда пользователь выполнил все задания</p>
              {formatMode === 'html' ? (
                <p>Форматирование: <code className="bg-tg-bg px-1 rounded">&lt;b&gt;жирный&lt;/b&gt;</code> <code className="bg-tg-bg px-1 rounded">&lt;i&gt;курсив&lt;/i&gt;</code></p>
              ) : (
                <p>Форматирование: <code className="bg-tg-bg px-1 rounded">**жирный**</code> <code className="bg-tg-bg px-1 rounded">*курсив*</code></p>
              )}
            </div>
            <div className="relative">
              <textarea
                value={completionMessageText[selectedLanguage] || ''}
                onChange={(e) => {
                  setCompletionMessageText({ ...completionMessageText, [selectedLanguage]: e.target.value })
                  setError('')
                }}
                onFocus={() => hapticFeedback('light')}
                spellCheck={false}
                className="w-full px-4 py-3 text-base border-2 border-transparent rounded-lg focus:border-[#5288c1] focus:outline-none transition-colors bg-tg-bg placeholder:text-tg-hint resize-none overflow-hidden"
                style={{
                  color: 'transparent',
                  caretColor: 'var(--tg-theme-text-color)',
                  WebkitTextFillColor: 'transparent'
                }}
                placeholder={`Сообщение о завершении на ${selectedLanguage === 'ru' ? 'русском' : 'английском'} языке`}
                rows={3}
              />
              <div
                className="absolute inset-0 px-4 py-3 text-base pointer-events-none whitespace-pre-wrap break-words overflow-hidden z-10"
                style={{
                  font: 'inherit',
                  lineHeight: 'inherit',
                  color: 'var(--tg-theme-text-color)'
                }}
                dangerouslySetInnerHTML={{
                  __html: (completionMessageText[selectedLanguage] || '')
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/;(fullname|username);/gi, (match) => {
                      return `<span style="background: linear-gradient(90deg, #ff0080, #ff8c00, #40e0d0, #4169e1, #9370db); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; text-decoration: none;">${match}</span>`
                    })
                }}
              />
            </div>
          </div>
        </div>

        {/* Reset to Default Button */}
        <button
          onClick={handleResetToDefault}
          className="w-full flex items-center justify-center gap-2 p-4 bg-tg-secondary-bg rounded-xl shadow-sm hover:bg-tg-hint/10 active:bg-tg-hint/20 transition-colors"
        >
          <RotateCcw className="w-5 h-5 text-tg-hint" />
          <span className="font-medium text-tg-text">Сбросить по умолчанию</span>
        </button>

        {/* Copy/Paste Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleCopyConfig}
            className="flex-1 flex items-center justify-center gap-2 p-4 bg-tg-secondary-bg rounded-xl shadow-sm hover:bg-tg-hint/10 active:bg-tg-hint/20 transition-colors"
          >
            <Copy className="w-5 h-5 text-[#5288c1]" />
            <span className="font-medium text-tg-text">Скопировать</span>
          </button>
          <button
            onClick={handlePasteConfig}
            disabled={!hasClipboard}
            className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-xl shadow-sm transition-colors ${
              hasClipboard
                ? 'bg-tg-secondary-bg hover:bg-tg-hint/10 active:bg-tg-hint/20'
                : 'bg-tg-secondary-bg/50 cursor-not-allowed'
            }`}
          >
            <ClipboardPaste className={`w-5 h-5 ${hasClipboard ? 'text-green-500' : 'text-tg-hint/50'}`} />
            <span className={`font-medium ${hasClipboard ? 'text-tg-text' : 'text-tg-hint/50'}`}>Вставить</span>
          </button>
        </div>

        {/* Clipboard Message */}
        {clipboardMessage && (
          <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-blue-500" />
              <p className="text-sm text-blue-500">{clipboardMessage}</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {showSuccess && (
          <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              <p className="text-sm text-green-600">Настройки успешно сохранены</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Button Item Component with Drag Controls
interface ButtonItemProps {
  button: CustomBlockButton
  index: number
  isEditing: boolean
  selectedLanguage: string
  onEdit: (index: number) => void
  onDelete: (index: number) => void
  onSave: (index: number, button: CustomBlockButton) => void
  onCancelEdit: () => void
  onDragEnd: () => void
  hapticFeedback: (type: 'light' | 'medium' | 'heavy') => void
}

function ButtonItem({
  button,
  index,
  isEditing,
  selectedLanguage,
  onEdit,
  onDelete,
  onSave,
  onCancelEdit,
  onDragEnd,
  hapticFeedback
}: ButtonItemProps) {
  const dragControls = useDragControls()

  return (
    <Reorder.Item
      value={button}
      dragListener={false}
      dragControls={dragControls}
      as="div"
      className="relative select-none"
      initial={false}
      dragElastic={0}
      dragMomentum={false}
      dragTransition={{ bounceStiffness: 600, bounceDamping: 20 }}
      onDragEnd={onDragEnd}
      whileDrag={{
        scale: 1.02,
        zIndex: 1000,
        opacity: 0.9,
        cursor: 'grabbing'
      }}
    >
      {!isEditing ? (
        <div
          className="bg-tg-bg rounded-lg p-3 border-2 border-[#5288c1]/20"
          style={{
            transform: 'translateZ(0)',
            contain: 'layout style paint',
            willChange: 'transform'
          }}
        >
          <div className="flex items-center gap-3">
            {/* Drag Handle - Only draggable part */}
            <div
              className="flex-shrink-0 text-tg-hint cursor-grab active:cursor-grabbing touch-none select-none"
              onPointerDown={(e) => {
                e.preventDefault()
                hapticFeedback('light')
                dragControls.start(e)
              }}
              style={{ touchAction: 'none', WebkitTouchCallout: 'none' }}
            >
              <GripVertical className="w-5 h-5" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-tg-text truncate">
                {button.text[selectedLanguage] || button.text['ru'] || button.text['en'] || '(пусто)'}
              </div>
              <div className="text-xs font-mono text-tg-hint truncate mt-0.5">
                {button.callback_data}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => onEdit(index)}
                className="p-2 hover:bg-blue-500/10 rounded-lg transition-colors"
              >
                <Edit2 className="w-4 h-4 text-blue-500" />
              </button>

              <button
                onClick={() => onDelete(index)}
                className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <ButtonEditor
          button={button}
          selectedLanguage={selectedLanguage}
          onSave={(updatedButton) => onSave(index, updatedButton)}
          onCancel={onCancelEdit}
          hapticFeedback={hapticFeedback}
        />
      )}
    </Reorder.Item>
  )
}

// Button Editor Component
interface ButtonEditorProps {
  button: CustomBlockButton
  selectedLanguage: string
  onSave: (button: CustomBlockButton) => void
  onCancel: () => void
  hapticFeedback: (type: 'light' | 'medium' | 'heavy') => void
}

function ButtonEditor({ button, selectedLanguage, onSave, onCancel, hapticFeedback }: ButtonEditorProps) {
  const [localButton, setLocalButton] = useState<CustomBlockButton>(button)

  return (
    <div className="bg-green-500/10 border-2 border-green-500/30 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-start gap-2">
        <button
          onClick={() => {
            hapticFeedback('light')
            onCancel()
          }}
          className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Отмена
        </button>
        <button
          onClick={() => {
            hapticFeedback('medium')
            onSave(localButton)
          }}
          className="px-3 py-1.5 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
        >
          Сохранить
        </button>
      </div>

      {/* Multi-Language Text */}
      <div>
        <label className="block text-xs text-tg-hint mb-1.5">
          Текст кнопки ({selectedLanguage.toUpperCase()})
        </label>
        <input
          type="text"
          value={localButton.text[selectedLanguage] || ''}
          onChange={(e) => {
            setLocalButton({
              ...localButton,
              text: { ...localButton.text, [selectedLanguage]: e.target.value }
            })
            hapticFeedback('light')
          }}
          className="w-full px-3 py-2 bg-tg-bg text-tg-text rounded-lg border-2 border-transparent focus:border-green-500 focus:outline-none"
          placeholder="Текст кнопки"
        />
      </div>

      {/* Callback Data */}
      <div>
        <label className="block text-xs text-tg-hint mb-1.5">
          Callback Data
        </label>
        <input
          type="text"
          value={localButton.callback_data}
          onChange={(e) => {
            setLocalButton({ ...localButton, callback_data: e.target.value })
            hapticFeedback('light')
          }}
          className="w-full px-3 py-2 bg-tg-bg text-tg-text rounded-lg border-2 border-transparent focus:border-green-500 focus:outline-none font-mono text-sm"
          placeholder="button_action"
        />
      </div>
    </div>
  )
}
